import { useCallback } from "react";
import type { BaseEntity, SimpleTableConfig, FieldValue } from "../../types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import { handleCellEdit, handleCellSave, handleCellCancel } from "../../utils";

interface UseInlineEditCallbacksProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  data: TData[];
  editValue: string;
  appUser: AuthenticatedUser;
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; fieldId: string } | null>>;
  setEditValue: React.Dispatch<React.SetStateAction<string>>;
  setSavingCell: React.Dispatch<React.SetStateAction<{ rowId: string; fieldId: string } | null>>;
}

export function useInlineEditCallbacks<TData extends BaseEntity>({
  config,
  data,
  editValue,
  appUser,
  onInlineEdit,
  setEditingCell,
  setEditValue,
  setSavingCell,
}: UseInlineEditCallbacksProps<TData>) {
  const handleCellEditCallback = useCallback(
    (rowId: string, fieldId: string, currentValue: FieldValue) => {
      handleCellEdit(rowId, fieldId, currentValue, config.fields, setEditingCell, setEditValue);
    },
    [config.fields, setEditingCell, setEditValue]
  );

  const handleCellSaveCallback = useCallback(
    async (rowId: string, fieldId: string, newValue?: string) => {
      if (onInlineEdit) {
        // Use newValue if provided (e.g., from CalendarDateEdit), otherwise use editValue from state
        const valueToSave = newValue !== undefined ? newValue : editValue;
        await handleCellSave(
          rowId,
          fieldId,
          valueToSave,
          data,
          config.fields,
          onInlineEdit,
          appUser,
          setEditingCell,
          setEditValue,
          setSavingCell,
          config.displayName
        );
      }
    },
    [
      editValue,
      onInlineEdit,
      data,
      config.fields,
      config.displayName,
      appUser,
      setEditingCell,
      setEditValue,
      setSavingCell,
    ]
  );

  const handleCellCancelCallback = useCallback(() => {
    handleCellCancel(setEditingCell, setEditValue, setSavingCell);
  }, [setEditingCell, setEditValue, setSavingCell]);

  return {
    handleCellEditCallback,
    handleCellSaveCallback,
    handleCellCancelCallback,
  };
}
